import { useDispatch, useSelector } from "react-redux";
import { CustomizationProvider, CustomizationProviderProps } from "@twilio-paste/core/customization";
import { CSSProperties, FC, useEffect } from "react";
import { StylingGlobals } from "@twilio-paste/core/styling-library";

import { RootContainer } from "./RootContainer";
import { AppState, EngagementPhase } from "../store/definitions";
import { sessionDataHandler } from "../sessionDataHandler";
import { autoInitSession, initSession } from "../store/actions/initActions";
import { changeEngagementPhase } from "../store/actions/genericActions";

const AnyCustomizationProvider: FC<CustomizationProviderProps & { style: CSSProperties }> = CustomizationProvider;

export function WebchatWidget() {
    const { theme, glanceConfiguration, autoInitConfig } = useSelector((state: AppState) => ({
        theme: state.config.theme,
        glanceConfiguration: state.config.glance,
        autoInitConfig: state.config.autoInitConfig
    }));
    const dispatch = useDispatch();

    useEffect(() => {
        const data = sessionDataHandler.tryResumeExistingSession();
        if (data) {
            try {
                dispatch(
                    initSession({
                        token: data.token,
                        conversationSid: data.conversationSid,
                        glanceConfiguration
                    })
                );
            } catch (e) {
                // if initSession fails, go to changeEngagement phase - most likely there's something wrong with the store token or conversation sis
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            }
        }
        // If we found the autoInitConfig we should intiialize the webchat automatically
        else if (autoInitConfig) {
            try {
                const { name, email, query, ...preInitData } = autoInitConfig;

                dispatch(
                    autoInitSession({
                        name,
                        email,
                        query,
                        data: preInitData,
                        glanceConfiguration
                    })
                );
            } catch (e) {
                // if initSession fails, go to changeEngagement phase - most likely there's something wrong with the store token or conversation sis
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            }
        } else {
            // if no token is stored, got engagement form
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    }, [dispatch, glanceConfiguration, autoInitConfig]);

    return (
        <AnyCustomizationProvider
            baseTheme={theme?.isLight ? "default" : "dark"}
            theme={theme?.overrides}
            elements={{
                MESSAGE_INPUT: {
                    boxShadow: "none!important" as "none"
                },
                MESSAGE_INPUT_BOX: {
                    display: "inline-block",
                    boxShadow: "none"
                },
                ALERT: {
                    paddingTop: "space30",
                    paddingBottom: "space30"
                },
                BUTTON: {
                    "&[aria-disabled='true'][color='colorTextLink']": {
                        color: "colorTextLinkWeak"
                    }
                }
            }}
            style={{ minHeight: "100%", minWidth: "100%" }}
        >
            <StylingGlobals
                styles={{
                    body: {
                        backgroundColor: "inherit",
                        margin: "inherit",
                        fontSize: "inherit"
                    }
                }}
            />
            <RootContainer />
        </AnyCustomizationProvider>
    );
}
